"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, X, Sparkles } from "lucide-react";
import { Button } from "./ui/Button";

interface WelcomeCreditsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  credits?: number;
}

export function WelcomeCreditsPopup({ 
  isOpen, 
  onClose, 
  credits = 3 
}: WelcomeCreditsPopupProps) {
  const [shouldAutoDismiss, setShouldAutoDismiss] = useState(true);

  useEffect(() => {
    if (isOpen && shouldAutoDismiss) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000); // Auto-dismiss after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldAutoDismiss, onClose]);

  const handleManualClose = () => {
    setShouldAutoDismiss(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={handleManualClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ 
              type: "spring", 
              duration: 0.5,
              bounce: 0.3
            }}
            className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 max-w-md w-full border border-slate-200/50"
          >
            {/* Close Button */}
            <button
              onClick={handleManualClose}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={20} />
            </button>

            {/* Icon with Animation */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                type: "spring",
                delay: 0.2,
                duration: 0.6,
                bounce: 0.5
              }}
              className="w-20 h-20 mx-auto mb-6 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex items-center justify-center relative"
            >
              <Gift size={36} className="text-white" strokeWidth={2} />
              
              {/* Sparkles */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="absolute -top-2 -right-2"
              >
                <Sparkles size={20} className="text-yellow-400" fill="currentColor" />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.3 }}
                className="absolute -bottom-1 -left-2"
              >
                <Sparkles size={16} className="text-yellow-400" fill="currentColor" />
              </motion.div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-center"
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Welcome to Resume Aligned!
              </h2>
              
              <p className="text-slate-600 mb-2 text-lg">
                You've received <span className="font-bold text-blue-600">{credits} free credits</span>
              </p>
              
              <p className="text-slate-500 text-sm mb-6">
                to get started on your journey! 🎉
              </p>

              {/* CTA Button */}
              <Button
                onClick={handleManualClose}
                className="w-full rounded-full shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-shadow"
              >
                Got it!
              </Button>
            </motion.div>

            {/* Progress Bar for Auto-dismiss */}
            {shouldAutoDismiss && (
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 5, ease: "linear" }}
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-b-2xl origin-left"
              />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
